// Entity service for fetching CNSEntityHolder data
import { CNS_API_BASE } from "@/config";
import { CNSEntityHolder, CNSEntityHolderPage } from "@/models/cns";

const ENTITIES_URL = `${CNS_API_BASE}/conversations`;

/** Distinguishes an authoritative empty holder response from an unavailable holder read. */
export class ConversationEntitiesFetchError extends Error {
  readonly conversationId: string;
  readonly status?: number;
  readonly retriable: boolean;
  readonly cause?: unknown;

  constructor(input: {
    conversationId: string;
    message: string;
    status?: number;
    retriable: boolean;
    cause?: unknown;
  }) {
    super(input.message);
    this.name = "ConversationEntitiesFetchError";
    this.conversationId = input.conversationId;
    this.status = input.status;
    this.retriable = input.retriable;
    this.cause = input.cause;
  }
}

export function isConversationEntitiesFetchError(
  value: unknown,
): value is ConversationEntitiesFetchError {
  return value instanceof ConversationEntitiesFetchError;
}

function failedResponse(
  conversationId: string,
  response: Response,
): ConversationEntitiesFetchError {
  return new ConversationEntitiesFetchError({
    conversationId,
    status: response.status,
    retriable: response.status === 408 || response.status === 429 || response.status >= 500,
    message: `Failed to fetch conversation entities: ${response.status} ${response.statusText}`,
  });
}

function failedRequest(
  conversationId: string,
  error: unknown,
): ConversationEntitiesFetchError {
  if (isConversationEntitiesFetchError(error)) return error;
  return new ConversationEntitiesFetchError({
    conversationId,
    retriable: true,
    message: "Conversation entities are temporarily unavailable.",
    cause: error,
  });
}

export async function fetchEntitiesByConversationId(conversationId: string): Promise<CNSEntityHolder[]> {
  try {
    const response = await fetch(`${ENTITIES_URL}/${conversationId}/entities`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 404) {
      return [];
    }

    if (!response.ok) {
      throw failedResponse(conversationId, response);
    }

    return await response.json();
  } catch (error) {
    throw failedRequest(conversationId, error);
  }
}

export async function fetchEntitiesByConversationIdPaginated(
  conversationId: string, 
  page: number = 0, 
  size: number = 20
): Promise<CNSEntityHolderPage> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    const response = await fetch(`${ENTITIES_URL}/${conversationId}/entities?${params}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 404) {
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        first: true,
        last: true,
        number: 0,
        numberOfElements: 0,
        size: size,
        empty: true,
      };
    }

    if (!response.ok) {
      throw failedResponse(conversationId, response);
    }

    return await response.json();
  } catch (error) {
    throw failedRequest(conversationId, error);
  }
}

export function createEntityMap(entities: CNSEntityHolder[]): Map<string, CNSEntityHolder> {
  const entityMap = new Map<string, CNSEntityHolder>();
  entities.forEach(entity => {
    if (entity.toolInvoke?.toolCallId) {
      entityMap.set(entity.toolInvoke.toolCallId, entity);
    }
  });
  return entityMap;
}
